import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/** Merge class names with Tailwind conflict resolution. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

function hasStringMessage(value: unknown): value is { message: string } {
  try {
    return typeof value === "object" && value !== null && "message" in value && typeof (value as { message?: unknown }).message === "string";
  } catch {
    return false;
  }
}

export function looksLikeReactElement(value: unknown): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    "$$typeof" in value &&
    typeof (value as { $$typeof?: unknown }).$$typeof === "symbol"
  );
}

/**
 * Coerce any value to a string without calling `String(obj)` on arbitrary objects.
 * `String(Object.create(null))` and some objects with broken valueOf/toString throw
 * `TypeError: Cannot convert object to primitive value` (ToPrimitive / OrdinaryToPrimitive).
 */
function objectToDisplayString(value: object, fallback: string): string {
  if (looksLikeReactElement(value)) return fallback;
  if (Array.isArray(value)) {
    try {
      return value.map((item) => safePrimitive(item, fallback)).join(",");
    } catch {
      try {
        return JSON.stringify(value);
      } catch {
        return fallback;
      }
    }
  }
  if (value instanceof Date) return value.toISOString();
  try {
    return JSON.stringify(value);
  } catch {
    try {
      return Object.prototype.toString.call(value);
    } catch {
      return fallback;
    }
  }
}

function safePrimitive(value: unknown, fallback = ""): string | number {
  try {
    if (value == null) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
    if (typeof value === "boolean") return value ? "true" : "false";
    if (typeof value === "bigint") {
      try {
        return value.toString();
      } catch {
        return fallback;
      }
    }
    if (typeof value === "symbol") return value.description ?? fallback;
    if (typeof value === "function") return fallback;

    try {
      if (value instanceof Error) return value.message;
    } catch {
      // ignore
    }

    if (hasStringMessage(value)) return value.message;

    if (typeof value === "object") {
      return objectToDisplayString(value, fallback);
    }

    try {
      return String(value);
    } catch {
      return fallback;
    }
  } catch {
    return fallback;
  }
}

/**
 * Safely convert any value to a display string. Never throws.
 * Prevents "Cannot convert object to primitive value".
 */
export function toErrorDisplayMessage(value: unknown): string {
  const p = safePrimitive(value, "");
  return typeof p === "number" ? String(p) : p;
}

/** Like toErrorDisplayMessage — for general display (e.g. t() results that may be objects). */
export function toDisplayString(value: unknown, fallback = ""): string {
  const p = safePrimitive(value, fallback);
  return typeof p === "number" ? String(p) : p;
}

/**
 * Return a safe React node (string | number) — never an object.
 * Never throws; prevents "Cannot convert object to primitive value".
 */
export function toSafeReactNode(value: unknown, fallback = ""): string | number {
  return safePrimitive(value, fallback);
}
